import { useState } from "react";
import {
  formatPublicEventDate,
  isLibertyLongRange,
  workspaceEventNounPlural,
  workspaceMemberNoun,
  workspaceNoun
} from "../public-site/publicSiteUtils";
import { MemberPortalSectionTabs } from "./MemberPortalTabs";
import {
  RELEASE_FORM_ACTION_LABEL,
  backgroundCheckStatusCopy,
  memberPortalActionSettings,
  memberReviewFollowUpCopy,
  waiverRequiresSignature,
  waiverStatusCopy
} from "./memberPortalUtils";
import {
  memberReservationResourceName,
  rangeOpeningStatusLabel,
  reservationIsToday,
  resourceOpeningTimeLabel
} from "./memberReservationUtils";

export function MemberPortalDashboard({
  applicantStatus,
  isAuthenticated,
  onRequireAuth,
  publicPlans = [],
  publicReservations = [],
  reservationBusinessHours = null,
  reservationEligibility = null,
  range
}) {
  const workspace = workspaceNoun(range, "range");
  const memberNoun = workspaceMemberNoun(range, "member");
  const eventNounPlural = workspaceEventNounPlural(range, "events");
  const member = applicantStatus?.members?.find((item) => item.memberId === applicantStatus.current?.memberId);
  const intent = applicantStatus?.signupIntents?.find((item) => item.signupIntentId === applicantStatus.current?.signupIntentId)
    || applicantStatus?.signupIntents?.[0];
  const intentPlan = publicPlans.find((plan) => plan.membershipTypeId === intent?.membershipTypeId);
  const requirements = member?.review?.requirements || {};
  const waiver = requirements.waiver || applicantStatus?.membership?.reviewPolicy?.publicSignup?.waiver || {};
  const prequalification = requirements.prequalification || applicantStatus?.membership?.reviewPolicy?.publicSignup?.prequalification || {};
  const backgroundCheck = requirements.backgroundCheck || applicantStatus?.membership?.reviewPolicy?.publicSignup?.backgroundCheck || {};
  const backgroundCheckCopy = backgroundCheckStatusCopy(backgroundCheck);
  const waiverCopy = waiverStatusCopy(waiver);
  const active = member?.status === "active";
  const followUpCopy = memberReviewFollowUpCopy(member);
  const portalActions = memberPortalActionSettings(range);
  const [todaySection, setTodaySection] = useState("daily");
  const actionItems = [];
  const portalStatusItems = [];
  const balanceDueCents = Number(member?.membership?.balanceDueCents || 0);
  const balancePaymentStatus = member?.membership?.balancePaymentStatus || member?.stripe?.balancePaymentStatus || "";
  const paymentNeedsAction = balanceDueCents > 0 && balancePaymentStatus !== "paid";
  const activeReservations = (publicReservations || [])
    .filter((reservation) => !["cancelled", "completed", "no_show"].includes(String(reservation.status || "").toLowerCase()))
    .sort((left, right) => Date.parse(left.startsAt || left.createdAt || 0) - Date.parse(right.startsAt || right.createdAt || 0));
  const nowMs = Date.now();
  const todayReservations = activeReservations.filter(reservationIsToday);
  const todayReservation = todayReservations.find((reservation) => {
    const endsAt = Date.parse(reservation.endsAt || reservation.startsAt || 0);
    return Number.isFinite(endsAt) && endsAt >= nowMs;
  }) || todayReservations[0];
  const nextReservation = activeReservations.find((reservation) => {
    const endsAt = Date.parse(reservation.endsAt || reservation.startsAt || 0);
    return Number.isFinite(endsAt) && endsAt >= nowMs;
  });
  const bookingResources = reservationEligibility?.resources || [];
  const memberWaiverStatus = String(requirements.waiver?.status || "").trim().toLowerCase();
  const policyWaiverRequired = applicantStatus?.membership?.reviewPolicy?.publicSignup?.waiver?.required !== false;
  const waiverActionNeeded = Boolean(member && (
    waiverRequiresSignature(waiver) ||
    (policyWaiverRequired && !["completed", "accepted", "approved"].includes(memberWaiverStatus))
  ));
  const onboardingActionNeeded = portalActions.onboarding && member && prequalification.required !== false && prequalification.status !== "completed";
  const backgroundActionNeeded = portalActions.onboarding && member && backgroundCheck.required && backgroundCheck.status !== "completed";
  const actionCount = [
    paymentNeedsAction,
    waiverActionNeeded,
    onboardingActionNeeded,
    backgroundActionNeeded,
    Boolean(member && followUpCopy && !active)
  ].filter(Boolean).length;

  if (portalActions.billing && !member && intent?.checkout?.url) {
    actionItems.push({
      title: "Finish membership checkout",
      body: `Payment is still waiting for completion before the ${workspace} can review your membership.`,
      href: intent.checkout.url
    });
  }
  if (portalActions.membershipStatus && !member && !intent) {
    actionItems.push({
      title: "Choose a membership plan",
      body: `Start with one of the current plans before ${eventNounPlural} or member-only actions unlock.`,
      href: "#membership"
    });
  }
  if (member && waiverActionNeeded) {
    actionItems.push({
      title: waiverCopy.actionLabel || "Sign release form",
      body: waiverCopy.detail,
      href: "#member-documents"
    });
  }
  if (portalActions.onboarding && member && prequalification.required !== false && prequalification.status !== "completed") {
    actionItems.push({
      title: isLibertyLongRange(range) ? "Complete orientation steps" : "Complete onboarding steps",
      body: isLibertyLongRange(range)
        ? "Orientation, background check, and optional 1,000 yard qualification may be reviewed by the range."
        : `Submit the required ${memberNoun} onboarding responses for ${workspace} review.`,
      href: "#member-status"
    });
  }
  if (portalActions.onboarding && member && backgroundCheck.required && backgroundCheck.status !== "completed") {
    actionItems.push({
      title: "Background check pending",
      body: backgroundCheckCopy.detail,
      href: "#member-status"
    });
  }
  if (portalActions.membershipStatus && member && followUpCopy) {
    actionItems.push({
      title: followUpCopy.actionTitle,
      body: followUpCopy.actionBody,
      href: "#member-status"
    });
  }

  if (member) {
    portalStatusItems.push(
      {
        label: "Access",
        value: waiverActionNeeded ? "Not ready yet" : active ? "Ready to visit" : "In review",
        detail: waiverActionNeeded
          ? "Finish the release form before using a reserved area."
          : active ? `${memberNoun[0].toUpperCase()}${memberNoun.slice(1)} access is active for this ${workspace}.` : `The ${workspace} team will activate access when ready.`
      },
      {
        label: "Release form",
        value: waiverActionNeeded ? "Signature needed" : "Current",
        detail: waiverActionNeeded
          ? "Open Documents to review and sign the current form."
          : "No release-form action is showing right now."
      },
      {
        label: "Booking access",
        value: active && !waiverActionNeeded ? "Open in Visits" : "Locked",
        detail: active && !waiverActionNeeded
          ? bookingResources.length ? "Visits shows available areas, closures, and booking limits." : "Visits explains what is available at this range."
          : "Bookings do not clear access until required steps are complete."
      },
      {
        label: "Next check",
        value: actionCount > 0 ? `${actionCount} item${actionCount === 1 ? "" : "s"} to finish` : "Nothing urgent",
        detail: actionCount > 0
          ? "Open Next for the shortest path before arrival."
          : "Plan and Pay keep the full account details."
      }
    );
  } else {
    portalStatusItems.push({
      label: "Membership",
      value: intent?.paymentStatus === "paid" ? "Payment received" : intent ? "Pending signup" : "No membership yet",
      detail: intentPlan?.name || (intent ? "Selected membership plan" : "Select or complete a plan")
    });
  }
  const todayNextStep = !member
    ? intent?.paymentStatus === "paid"
      ? {
          title: "Wait for your member record",
          body: `Your payment is recorded. The ${workspace} will connect it to your member access after review.`,
          detail: "Signup is moving"
        }
      : intent?.checkout?.url
        ? {
            title: "Finish membership checkout",
            body: `Complete checkout so the ${workspace} can review your membership.`,
            detail: "Payment needed",
            href: intent.checkout.url
          }
        : intent
          ? {
              title: "Membership request received",
              body: `Your request is with the ${workspace} team. Payment instructions and member access will appear here after review.`,
              detail: "Review pending",
              href: "#member-status"
            }
        : {
            title: "Choose a membership plan",
            body: `Start with a membership plan before member-only ${eventNounPlural} or visits unlock.`,
            detail: "Start membership",
            href: "#membership"
          }
    : waiverActionNeeded
      ? {
          title: "Release form needed",
          body: "Review the current form and complete the signature step before your next visit.",
          detail: "Action needed",
          href: "#member-documents"
        }
    : active
      ? {
          title: "Plan your next visit",
          body: `Use Events${memberPortalActionSettings(range).reservations ? " or Visits" : ""} to see what is available for this membership.`,
          detail: "Member access active",
          href: "#events"
        }
      : {
          title: "Watch for range approval",
          body: `Your payment and plan are recorded. The ${workspace} team will activate access after final review.`,
          detail: "No action needed right now",
          href: "#member-status"
        };
  const todayHelpfulLink = member?.status === "active"
    ? {
        title: "Before you arrive",
        body: "Check current rules, shared files, and any published classes before your next visit.",
        href: "#member-documents"
      }
    : {
        title: "Review membership details",
        body: "Use Membership to see the plan, payment, term, review state, and any required steps.",
        href: "#member-status"
      };
  const dailyDetail = !member
    ? "Start or finish membership before daily member tools unlock."
    : actionCount > 0
      ? `${actionCount} item${actionCount === 1 ? "" : "s"} need attention before your next visit.`
      : active
        ? "No payment or document action is showing right now."
        : `The ${workspace} team will activate access after review.`;
  const dailyOpeningStatus = member?.status === "active" && !waiverActionNeeded
    ? rangeOpeningStatusLabel(reservationBusinessHours)
    : "";
  const dailyOpeningDetail = reservationBusinessHours
    ? resourceOpeningTimeLabel({}, reservationBusinessHours)
    : bookingResources.length
      ? "Area hours are shown with each bookable area."
      : `Ask the ${workspace} for today's hours if they are not shown here.`;
  const dailyAnswer = !member
    ? {
        tone: "blocked",
        label: "Membership needed",
        title: "Start membership before visiting.",
        detail: "Once membership is connected, today's hours, reservations, and booking options will appear here.",
        action: "Open membership",
        href: "#member-status"
      }
    : waiverActionNeeded
      ? {
          tone: "blocked",
          label: "Action needed",
          title: "Sign the release form before your visit.",
          detail: "Your bookings stay visible, but they do not clear access until the current release form is signed.",
          action: RELEASE_FORM_ACTION_LABEL,
          href: "#member-documents"
        }
      : member?.status === "active"
        ? {
            tone: dailyOpeningStatus.toLowerCase().includes("closed") ? "closed" : "ready",
            label: "Today",
            title: dailyOpeningStatus || "Check today's range status.",
            detail: `${dailyOpeningDetail}${bookingResources.length ? " Use Visits to choose an eligible future time." : ""}`,
            action: bookingResources.length ? "Open Visits" : "Check visits",
            href: "#member-visits"
          }
        : {
            tone: "pending",
            label: "Membership review",
            title: "Wait for range approval before visiting.",
            detail: `The ${workspace} team will activate access after final review.`,
            action: "Open membership",
            href: "#member-status"
          };
  const arrivalOpeningTitle = member?.status === "active"
    ? "Open status"
    : "Access status";
  const arrivalOpeningValue = member?.status === "active"
    ? waiverActionNeeded
      ? "Release form needed"
      : dailyOpeningStatus
    : member
      ? "Waiting for approval"
      : "Membership needed";
  const arrivalOpeningDetail = !member
    ? "Start or connect membership before range access and booking details unlock."
    : waiverActionNeeded
      ? RELEASE_FORM_ACTION_LABEL
    : dailyOpeningDetail;
  const arrivalReservationValue = todayReservation
    ? memberReservationResourceName(todayReservation)
    : todayReservations.length
      ? "Reserved today"
      : "None today";
  const arrivalReservationDetail = todayReservation
    ? `${formatPublicEventDate(todayReservation.startsAt)} - ${formatPublicEventDate(todayReservation.endsAt)}`
    : nextReservation
      ? `Next reservation: ${memberReservationResourceName(nextReservation)} ${formatPublicEventDate(nextReservation.startsAt)}`
      : "Today’s bookings will appear here immediately after you reserve.";
  const arrivalNextAction = waiverActionNeeded
    ? "Finish release form"
    : actionCount > 0
      ? "Action needed"
    : member?.status === "active"
      ? bookingResources.length
        ? "Book or review a visit"
        : "Check visits"
      : "Open membership";
  const arrivalNextDetail = waiverActionNeeded
    ? "Open Documents to review the form, then complete the signature step."
    : actionCount > 0
      ? dailyDetail
    : member?.status === "active"
      ? bookingResources.length
        ? "Use Visits for booking details, available areas, and requirements."
        : `Visits explains why online booking is not open for this ${workspace} yet.`
      : "Membership shows plan, payment, release form, and review status.";
  return (
    <section className="member-portal-dashboard" id="member-dashboard">
      <div className="member-portal-dashboard-header">
        <div>
          <span className="public-section-kicker">Today</span>
          <h2>Can I use the range today?</h2>
          <p>
            Check access, today’s bookings, and the next required step.
          </p>
        </div>
        {!isAuthenticated && (
          <button type="button" className="secondary-button" onClick={onRequireAuth}>
            Sign in
          </button>
        )}
      </div>
      {waiverActionNeeded && (
        <div className="member-decision-banner is-blocked">
          <span>Not yet</span>
          <strong>Sign the release form before your visit.</strong>
          <a className="public-primary-link" href="#member-documents">{RELEASE_FORM_ACTION_LABEL}</a>
        </div>
      )}
      <div className={`member-today-answer is-${dailyAnswer.tone}`}>
        <span>{dailyAnswer.label}</span>
        <strong>{dailyAnswer.title}</strong>
        <small>{dailyAnswer.detail}</small>
        <a className={dailyAnswer.tone === "blocked" ? "public-primary-link" : "secondary-button"} href={dailyAnswer.href}>
          {dailyAnswer.action}
        </a>
      </div>
      <MemberPortalSectionTabs
        activeSection={todaySection}
        onSelect={setTodaySection}
        sections={[
          { id: "daily", label: "Today", detail: "Hours and bookings" },
          { id: "status", label: "Status", detail: "Membership and forms" },
          { id: "next", label: "Next", detail: "Helpful actions" }
        ]}
      />
      {todaySection === "daily" && (
        <>
          <div className="member-arrival-strip" aria-label="Today arrival summary">
            <a href={member ? "#member-visits" : "#member-status"}>
              <span>{arrivalOpeningTitle}</span>
              <strong>{arrivalOpeningValue}</strong>
              <small>{arrivalOpeningDetail}</small>
            </a>
            <a href="#member-visits">
              <span>Today's reservation</span>
              <strong>{arrivalReservationValue}</strong>
              <small>{arrivalReservationDetail}</small>
            </a>
            <a href={waiverActionNeeded ? "#member-documents" : actionCount > 0 ? "#member-status" : member?.status === "active" ? "#member-visits" : "#member-status"}>
              <span>Next action</span>
              <strong>{arrivalNextAction}</strong>
              <small>{arrivalNextDetail}</small>
            </a>
          </div>
          <div className="member-today-visit-handoff">
            <div>
              <span>Visits</span>
              <strong>
                {bookingResources.length
                  ? `${bookingResources.length} reservable area${bookingResources.length === 1 ? "" : "s"} published`
                  : "Online booking is not published yet"}
              </strong>
              <small>
                {nextReservation
                  ? "Open Visits to review booking limits, available areas, and future reservations."
                  : "Open Visits to see available areas, closures, requirements, and booking options."}
              </small>
            </div>
            <a className="secondary-button" href="#member-visits">Open Visits</a>
          </div>
        </>
      )}
      {todaySection === "status" && (
        <div className="member-portal-status-grid">
          {portalActions.membershipStatus && portalStatusItems.map((item) => (
            <div key={item.label}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
              <small>{item.detail}</small>
            </div>
          ))}
        </div>
      )}
      {todaySection === "next" && (
        <div className="member-portal-focus-row">
          <a href={todayNextStep.href || "#member-dashboard"}>
            <span>Next step</span>
            <strong>{todayNextStep.title}</strong>
            <small>{todayNextStep.body}</small>
            <em>{todayNextStep.detail}</em>
          </a>
          <a href={todayHelpfulLink.href}>
            <span>Helpful now</span>
            <strong>{todayHelpfulLink.title}</strong>
            <small>{todayHelpfulLink.body}</small>
            <em>Open related tab</em>
          </a>
        </div>
      )}
      {todaySection === "next" && actionItems.length > 0 && (
        <div className="member-portal-actions">
          {actionItems.map((item) => (
            <a href={item.href} key={item.title}>
              <strong>{item.title}</strong>
              <span>{item.body}</span>
            </a>
          ))}
        </div>
      )}
    </section>
  );
}
